import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormRoot,
  FormTextarea,
  zodResolver,
} from "@devhop/ui";
import { ArrowLeft, Banknote, Send } from "lucide-react";
import { useMemo } from "react";
import * as RHF from "react-hook-form";
import { z } from "zod";

const AdvanceRequestSchema = z.object({
  requestDate: z.string().min(1, "ກະລຸນາເລືອກວັນທີ່ຍື່ນ"),
  amount: z
    .string()
    .min(1, "ກະລຸນາໃສ່ຈຳນວນ")
    .refine((v) => Number(v) > 0, "ຈຳນວນຕ້ອງຫຼາຍກວ່າ 0"),
  reason: z.string(),
});

export type AdvanceRequestFormValues = {
  requestDate: string;
  amount: string;
  reason: string;
};

export type AdvanceRequestFormProps = {
  onSubmit: (values: AdvanceRequestFormValues) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
};

function formatLAK(v: string) {
  const n = Number(v);
  if (!n || n <= 0) return null;
  return new Intl.NumberFormat("lo-LA").format(n) + " ກີບ";
}

export function AdvanceRequestForm({
  onSubmit,
  onCancel,
  isSubmitting,
}: AdvanceRequestFormProps) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const methods = RHF.useForm<AdvanceRequestFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(AdvanceRequestSchema) as any,
    defaultValues: {
      requestDate: today,
      amount: "",
      reason: "",
    },
  });

  const amountValue = RHF.useWatch({
    control: methods.control,
    name: "amount",
  });
  const formatted = formatLAK(amountValue ?? "");

  return (
    <FormRoot<AdvanceRequestFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="w-full"
    >
      <Card className="w-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Banknote className="h-4 w-4" />
            ຟອມຄຳຂໍເບິກເງິນເດືອນລ່ວງໜ້າ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormInput
            name="requestDate"
            label="ວັນທີ່ຍື່ນ"
            type="date"
            requiredMark
            max={today}
          />

          <div>
            <FormInput
              name="amount"
              label="ຈຳນວນທີ່ຕ້ອງການ (ກີບ)"
              type="number"
              min={1}
              step={1000}
              placeholder="0"
              requiredMark
            />
            {formatted && (
              <p className="mt-1 text-muted-foreground text-sm">{formatted}</p>
            )}
          </div>

          <FormTextarea
            name="reason"
            label="ເຫດຜົນ (ບໍ່ບັງຄັບ)"
            rows={3}
            placeholder="ລະບຸເຫດຜົນ..."
          />

          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
            <p className="font-medium">ໝາຍເຫດ</p>
            <ul className="mt-1 list-inside list-disc space-y-1">
              <li>ສາມາດມີຄຳຂໍທີ່ຢູ່ໃນລະຫວ່າງດຳເນີນການໄດ້ຄັ້ງລະ 1 ລາຍການ</li>
              <li>ຈຳນວນທີ່ໄດ້ຮັບອາດໜ້ອຍກວ່າທີ່ຂໍ ຂຶ້ນກັບດຸນພິນິດຂອງ HR</li>
              <li>ເງິນຈະຖືກຫັກຄືນຕາມແຜນທີ່ HR ກຳນົດ</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Sticky footer */}
      <div className="sticky bottom-0 z-10 mt-8 flex items-center justify-between gap-4 border-t bg-background/95 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          <ArrowLeft className="h-4 w-4" />
          ຍົກເລີກ
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          <Send className="h-4 w-4" />
          {isSubmitting ? "ກຳລັງສົ່ງ..." : "ຍື່ນຄຳຂໍ"}
        </Button>
      </div>
    </FormRoot>
  );
}
